/* globals PasswordCredential */
import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { AUTH_ENDPOINTS, API_URL } from "../config";

import { useMobile } from "../styles/commonHooks";
import { getThemeVars, globalAnimationStyles } from "../styles/theme";

import linkedinIcon from "../assets/chat_page/Frame_linkedin.svg";

const calculatePasswordStrength = (pwd = "") => {
  if (!pwd) return 0;
  let score = 0;
  if (pwd.length >= 8) score += 20;
  if (pwd.length >= 12) score += 15;
  if (pwd.length >= 16) score += 15;
  if (/[A-Z]/.test(pwd)) score += 15;
  if (/[a-z]/.test(pwd)) score += 15;
  if (/[0-9]/.test(pwd)) score += 15;
  if (/[^A-Za-z0-9]/.test(pwd)) score += 15;
  return Math.min(100, score);
};

const EyeIcon = ({ isOpen, color }) => (
  isOpen ? (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ) : (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  )
);

// ── desktop-only helpers ────────────────────────────────────────────────────
const Sparkline = ({ color, points }) => {
  const h = 36;
  const w = 160;
  const max = Math.max(...points);
  const min = Math.min(...points);
  const range = max - min || 1;
  const normalize = (p) => h - ((p - min) / range) * (h - 6) - 3;
  const step = w / (points.length - 1);
  const pathD = points
    .map((p, i) => `${i === 0 ? "M" : "L"} ${i * step} ${normalize(p)}`)
    .join(" ");
  const areaD = `${pathD} L ${(points.length - 1) * step} ${h} L 0 ${h} Z`;
  const id = `sg-${color.replace("#", "")}`;

  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ display: "block", flexShrink: 0 }}>
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.4" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={areaD} fill={`url(#${id})`} />
      <path d={pathD} fill="none" stroke={color} strokeWidth="1.8" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
};

const SPARK_PRESETS = [
  [6, 9, 8, 12, 14, 13, 18, 22, 20, 26, 24, 30],
  [10, 12, 11, 14, 17, 15, 20, 18, 24, 22, 28, 26],
  [4, 5, 6, 5, 7, 8, 9, 11, 10, 13, 15, 18],
];

const STAT_COLORS = ["#a78bfa", "#22d3ee", "#4ade80"];

export default function RegisterPage({
  lang,
  theme,
  translations,
  onLogin,
  isSamsungBrowser = false,
}) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isMobile = useMobile();
  const themeVars = getThemeVars(theme);

  const isLightTheme = theme === "light";

  // ── theme tokens for MOBILE (from original file 1) ──────────────────────────
  const inputBg = isSamsungBrowser
    ? "rgba(255,255,255,0.55)"
    : isLightTheme ? "rgba(255,255,255,0.42)" : "rgba(255, 255, 255, 0.09)";
  const inputFocusBg = isSamsungBrowser
    ? "rgba(255,255,255,0.75)"
    : isLightTheme ? "rgba(255,255,255,0.65)" : "rgba(255, 255, 255, 0.16)";
  const inputFocusShadow = isSamsungBrowser
    ? `0 0 0 3px ${themeVars.accent}40`
    : isLightTheme
    ? `0 0 0 3px ${themeVars.accent}35`
    : `0 0 0 3px ${themeVars.accent}50`;
  const panelBg = themeVars.subtleBg;
  const panelBorder = "none";
  const inputBorder = isSamsungBrowser
    ? "1px solid rgba(99,102,241,0.25)"
    : isLightTheme ? "1px solid rgba(99,102,241,0.18)" : "none";
  const textColor = isLightTheme ? "#1f2937" : themeVars.fg;
  const iconColor = isLightTheme ? "#4b5563" : "#ffffff";
  const oauthPanelBg = isLightTheme
    ? "rgba(255,255,255,0.35)"
    : "rgba(255, 255, 255, 0.09)";
  const oauthPanelBorder = isLightTheme
    ? `1px solid ${themeVars.accent}40`
    : `1px solid ${themeVars.accent}40`;

  // ── theme tokens for DESKTOP (from file 2) ──────────────────────────────────
  const BG        = isLightTheme ? "#f0f1f5" : "#0d0f18";
  const PANEL_BG  = isLightTheme ? "rgba(255,255,255,0.55)" : "rgba(255,255,255,0.028)";
  const CARD_BG   = isLightTheme ? "rgba(255,255,255,0.7)"  : "rgba(255,255,255,0.032)";
  const BORDER    = isLightTheme ? "rgba(99,102,241,0.14)"  : "rgba(255,255,255,0.06)";
  const INPUT_BG  = isLightTheme ? "rgba(255,255,255,0.6)"  : "rgba(255,255,255,0.06)";
  const INPUT_FOCUS_BG = isLightTheme ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.1)";
  const INPUT_SHADOW   = `0 0 0 2px ${themeVars.accent}40`;
  const TEXT      = isLightTheme ? "#111827" : "#e6e8ee";
  const MUTED     = isLightTheme ? "rgba(17,24,39,0.5)"  : "rgba(230,232,238,0.5)";
  const ICON_CLR  = isLightTheme ? "#6b7280" : "rgba(230,232,238,0.6)";

  // ── state ─────────────────────────────────────────────────────────────────
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [inviteCode, setInviteCode] = useState(localStorage.getItem("inviteCode") || "");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [agreePrivacy, setAgreePrivacy] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [error, setError] = useState("");
  const [inviteCodeValid, setInviteCodeValid] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [termsError, setTermsError] = useState("");
  const [isButtonPressed, setIsButtonPressed] = useState(false);
  const [isInviteCodeAutoFilled, setIsInviteCodeAutoFilled] = useState(false);

  // ── Live stats for desktop left panel ────────────────────────────────────
  const [registerStats, setRegisterStats] = useState(null);

  const fetchStats = useCallback(() => {
    fetch(`${API_URL}/api/public/stats`)
      .then(r => r.json())
      .then(d => {
        if (d && d.show_register === false) {
          setRegisterStats(null);
        } else if (d && d.users != null) {
          setRegisterStats(d);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchStats();
    // Рефетч при возвращении на вкладку — нужен если модератор менял настройки
    const onVisible = () => { if (document.visibilityState === "visible") fetchStats(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [fetchStats]);

  const statRows = registerStats
    ? [
        { n: registerStats.users,   l: "members", sub: "joined to date",        color: STAT_COLORS[0], spark: SPARK_PRESETS[0] },
        { n: registerStats.matches, l: "matches", sub: "introductions accepted", color: STAT_COLORS[1], spark: SPARK_PRESETS[1] },
        { n: registerStats.events,  l: "events",  sub: "hosted in 2026",         color: STAT_COLORS[2], spark: SPARK_PRESETS[2] },
      ]
    : null;

  // ── validation ────────────────────────────────────────────────────────────
  const validateEmail = useCallback(
    (value) => {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!value.trim()) {
        setEmailError(translations[lang]?.emailRequired || "Email is required");
        return false;
      }
      if (!emailRegex.test(value.trim())) {
        setEmailError(translations[lang]?.invalidEmail || "Invalid email format");
        return false;
      }
      setEmailError("");
      return true;
    },
    [lang, translations]
  );

  const validateInviteCode = useCallback(async (code) => {
    if (!code || !code.trim()) {
      setInviteCodeValid(false);
      return false;
    }
    try {
      const encodedCode = encodeURIComponent(code.trim());
      const res = await axios.get(`${API_URL}/api/invitation/check/${encodedCode}`);
      setInviteCodeValid(res.data.valid);
      return res.data.valid;
    } catch {
      setInviteCodeValid(false);
      return false;
    }
  }, []);

  const validateTerms = useCallback(() => {
    if (!agreePrivacy) {
      setTermsError(translations[lang]?.termsRequired || "Please accept the Privacy Policy");
      return false;
    }
    setTermsError("");
    return true;
  }, [agreePrivacy, lang, translations]);

  // ── effects ───────────────────────────────────────────────────────────────
  useEffect(() => {
    const tokenFromUrl = searchParams.get("token");
    const providerFromUrl = searchParams.get("provider");
    const actionFromUrl = searchParams.get("action");
    const errorFromUrl = searchParams.get("error");

    if (tokenFromUrl && providerFromUrl && actionFromUrl === "register") {
      console.log(`✅ OAuth registration successful via ${providerFromUrl}`);
      onLogin(tokenFromUrl);
      navigate("/chat");
      localStorage.removeItem("oauthPendingRegistration");
      localStorage.removeItem("oauthInviteCode");
      localStorage.removeItem("inviteCode");
      return;
    }

    if (errorFromUrl) {
      const messageFromUrl = searchParams.get("message");
      setError(messageFromUrl || translations[lang].oauthError || "OAuth authorization failed");
      return;
    }

    const codeFromUrl = searchParams.get("invite_code");
    if (codeFromUrl) {
      setInviteCode(codeFromUrl);
      setIsInviteCodeAutoFilled(true);
      localStorage.setItem("inviteCode", codeFromUrl);
      validateInviteCode(codeFromUrl);
    }

    const needInvite = searchParams.get("need_invite");
    const providerFromUrlPending = searchParams.get("provider");
    if (needInvite === "true" && providerFromUrlPending) {
      const emailFromUrl = searchParams.get("email");
      const nameFromUrl = searchParams.get("name");
      const oauthIdFromUrl = searchParams.get("oauth_id");
      const avatarFromUrl = searchParams.get("avatar_url");

      localStorage.setItem("oauthPendingRegistration", JSON.stringify({
        email: emailFromUrl,
        name: nameFromUrl,
        provider: providerFromUrlPending,
        oauth_id: oauthIdFromUrl,
        avatar_url: avatarFromUrl
      }));

      const savedInviteCode = localStorage.getItem("oauthInviteCode");
      if (savedInviteCode) {
        setInviteCode(savedInviteCode);
        setIsInviteCodeAutoFilled(true);
      } else {
        setError("Please enter invite code to complete registration");
      }
    }
  }, [searchParams, validateInviteCode, onLogin, navigate, translations, lang]);

  useEffect(() => {
    if (inviteCode.trim()) {
      const id = setTimeout(() => validateInviteCode(inviteCode), 500);
      return () => clearTimeout(id);
    } else {
      setInviteCodeValid(false);
    }
  }, [inviteCode, validateInviteCode]);

  useEffect(() => {
    setPasswordStrength(calculatePasswordStrength(password));
  }, [password]);

  // ── handlers ──────────────────────────────────────────────────────────────
  const handleInviteCodeChange = (e) => {
    const value = e.target.value;
    setInviteCode(value);
    if (isInviteCodeAutoFilled) {
      setIsInviteCodeAutoFilled(false);
    }
  };

  const triggerPasswordSave = async () => {
    if (!("PasswordCredential" in window) || !navigator?.credentials?.store) return;
    try {
      const credential = new PasswordCredential({
        id: email.trim().toLowerCase(),
        password: password,
        name: username.trim() || email.trim(),
      });
      await navigator.credentials.store(credential);
    } catch {}
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setEmailError("");
    setConfirmPasswordError("");
    setTermsError("");

    let hasError = false;

    if (!validateEmail(email)) {
      hasError = true;
    }

    const p1 = password.trim();
    const p2 = confirmPassword.trim();
    if (p1 !== p2) {
      setConfirmPasswordError(translations[lang]?.passwordMismatch || "Passwords do not match");
      hasError = true;
    } else if (p1 === "") {
      setConfirmPasswordError("Password cannot be empty");
      hasError = true;
    }

    if (!validateTerms()) {
      hasError = true;
    }

    const trimmedCode = inviteCode.trim();
    if (!trimmedCode) {
      setError(translations[lang]?.inviteCodeRequired || "Invite code required");
      hasError = true;
    } else if (!(await validateInviteCode(trimmedCode))) {
      setError(translations[lang]?.invalidInviteCode || "Invalid invite code");
      hasError = true;
    }

    // Сила пароля
    if (passwordStrength < 40) {
      setError(translations[lang]?.weakPassword || "Password is too weak");
      hasError = true;
    }

    if (hasError) return;

    try {
      await axios.post(`${API_URL}${AUTH_ENDPOINTS.REGISTER}`, {
        email,
        password: p1,
        username,
        name: username,
        invite_code: trimmedCode,
      });

      const loginData = new URLSearchParams();
      loginData.append("username", email);
      loginData.append("password", p1);

      const loginRes = await axios.post(`${API_URL}${AUTH_ENDPOINTS.LOGIN}`, loginData, {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      });

      const token = loginRes.data.access_token;
      if (!token) throw new Error("Auto-login failed");

      await triggerPasswordSave();
      onLogin(token);
      localStorage.removeItem("inviteCode");
      localStorage.removeItem("oauthPendingRegistration");
      localStorage.removeItem("oauthInviteCode");
      localStorage.removeItem("_was_logged_out");
      navigate("/chat");
    } catch (err) {
      let msg = translations[lang]?.error || "Error";
      if (err.response?.data?.detail) msg = err.response.data.detail;
      setError(msg);
    }
  };

  const handleSocialRegister = (provider) => {
    if (!inviteCode.trim() || !inviteCodeValid) {
      setError(translations[lang]?.inviteCodeRequired || "Invite code required");
      return;
    }
    localStorage.setItem("oauthInviteCode", inviteCode);
    window.location.href = `/api/auth/${provider}/login?mode=register&invite_code=${encodeURIComponent(inviteCode)}`;
  };

  const pendingOAuth = localStorage.getItem("oauthPendingRegistration");
  const oauthProvider = pendingOAuth ? JSON.parse(pendingOAuth).provider : null;

  // ── strength helpers for MOBILE (from file 1) ──────────────────────────────
  const getStrengthColor = (strength) => {
    if (strength === 0) return isLightTheme ? "#166534" : "#14532d";
    if (strength <= 33) return isLightTheme ? "#ef4444" : "#f87171";
    if (strength <= 66) return isLightTheme ? "#f59e0b" : "#f59e0b";
    return isLightTheme ? "#10b981" : "#4ade80";
  };

  const getStrengthText = (strength) => {
    if (strength === 0) return translations[lang]?.empty || "Empty";
    if (strength <= 33) return translations[lang]?.weak || "Weak";
    if (strength <= 66) return translations[lang]?.medium || "Medium";
    return translations[lang]?.strong || "Strong";
  };

  // ── strength helpers for DESKTOP (from file 2) ─────────────────────────────
  const strengthColor = (() => {
    if (passwordStrength === 0) return "#374151";
    if (passwordStrength <= 33) return "#ef4444";
    if (passwordStrength <= 66) return "#f59e0b";
    return "#4ade80";
  })();

  const strengthLabel = (() => {
    if (passwordStrength === 0) return translations[lang]?.empty  || "Empty";
    if (passwordStrength <= 33) return translations[lang]?.weak   || "Weak";
    if (passwordStrength <= 66) return translations[lang]?.medium || "Medium";
    return translations[lang]?.strong || "Strong";
  })();

  // ── checkbox styles for MOBILE (from file 1) ───────────────────────────────
  const checkboxStyle = {
    width: "20px",
    height: "20px",
    borderRadius: "6px",
    border: `2px solid ${isLightTheme ? "#d1d5db" : `${themeVars.fg}66`}`,
    background: "transparent",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    flexShrink: 0,
    transition: "all 0.2s ease",
    boxShadow: isLightTheme ? "0 1px 3px rgba(0,0,0,0.07)" : "none"
  };

  const checkedStyle = {
    ...checkboxStyle,
    background: themeVars.accent,
    borderColor: themeVars.accent,
    boxShadow: isLightTheme ? `0 2px 4px ${themeVars.accent}30` : "none"
  };

  // ════════════════════════════════════════════════════════════════════════════
  // MOBILE BRANCH — preserved exactly as in original file 1
  // ════════════════════════════════════════════════════════════════════════════
  if (isMobile) {
    return (
      <>
        <style>{`
          ${globalAnimationStyles}
          @keyframes fadeInLanguage {
            from { opacity: 0.45; transform: translateY(18px); }
            to   { opacity: 1;    transform: translateY(0); }
          }
          @keyframes lavaDrift {
            0%, 100% { background-position: 0% 0%, 100% 100%, 50% 0%, 0% 100%, 50% 50%; }
            50%       { background-position: 100% 100%, 0% 0%, 0% 100%, 100% 0%, 75% 25%; }
          }
          @keyframes lavaDrift2 {
            0%, 100% { background-position: 0% 0%, 100% 100%, 50% 0%; }
            50%       { background-position: 100% 100%, 0% 0%, 100% 50%; }
          }
          .reg-page-wrap {
            min-height: 100dvh;
            width: 100vw;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 32px 16px;
            box-sizing: border-box;
            background: ${isLightTheme ? "#f3f4f8" : "#131720"};
          }
          @media (max-width: 600px) {
            .reg-page-wrap {
              align-items: flex-start;
              padding: 0;
              min-height: 100svh;
              height: auto;
              overflow: visible;
              background: ${panelBg};
            }
          }
          .reg-card {
            width: 100%;
            max-width: 440px;
            background: transparent;
            border-radius: 0;
            padding: 44px 36px 36px;
            box-sizing: border-box;
            box-shadow: none;
          }
          @media (max-width: 600px) {
            .reg-card {
              background: transparent;
              border-radius: 0;
              width: 100%;
              min-height: unset;
              height: auto;
              padding: 50px 20px 40px;
              box-shadow: none;
              display: flex;
              flex-direction: column;
              justify-content: flex-start;
            }
          }
        `}</style>

        <div className="reg-page-wrap" style={{ color: textColor }}>
          <div className="reg-card">

            <h2
              className="animate-on-mount"
              style={{
                margin: isMobile ? "24px 0 12px" : "0 0 28px",
                fontSize: isMobile ? "1.7rem" : "2.2rem",
                fontWeight: 700,
                textAlign: "center",
                letterSpacing: "-0.02em",
                ...(isSamsungBrowser
                  ? { color: "#3730a3", background: "none", WebkitTextFillColor: "unset" }
                  : isLightTheme
                  ? {
                      background: themeVars.btnBg,
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                      color: themeVars.accent,
                    }
                  : {
                      background: themeVars.btnBg,
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                    }),
              }}
            >
              {translations[lang]?.register || "Create Account"}
            </h2>

            {pendingOAuth && (
              <div
                className="animate-on-mount"
                style={{
                  background: oauthPanelBg,
                  borderRadius: "10px",
                  padding: "14px 18px",
                  marginBottom: "20px",
                  fontSize: "1rem",
                  color: isLightTheme ? "#1e40af" : themeVars.fg,
                  textAlign: "center",
                  border: oauthPanelBorder,
                  boxShadow: isLightTheme ? "0 2px 6px rgba(0,0,0,0.05)" : "none",
                }}
              >
                ℹ️ Completing {oauthProvider} registration. Please enter invite code.
              </div>
            )}

            <form
              key={lang}
              onSubmit={handleSubmit}
              autoComplete="on"
              style={{
                display: "flex",
                flexDirection: "column",
                gap: isMobile ? "12px" : "18px",
                animation: "fadeInLanguage 0.48s ease-out",
                width: "100%",
              }}
            >
              <div style={{ position: "relative" }}>
                <input
                  className="animate-on-mount"
                  type="text"
                  name="name"
                  autoComplete="given-name"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder={translations[lang]?.name || "Full Name"}
                  required
                  style={{
                    background: inputBg,
                    color: textColor,
                    border: inputBorder,
                    borderRadius: "10px",
                    padding: isMobile ? "13px 18px" : "16px 20px",
                    fontSize: "1.08rem",
                    outline: "none",
                    width: "100%",
                    transition: "all 0.28s ease",
                    boxShadow: isLightTheme ? "0 1px 3px rgba(0,0,0,0.07)" : "none",
                  }}
                  onFocus={(e) => {
                    e.target.style.background = inputFocusBg;
                    e.target.style.boxShadow = inputFocusShadow;
                  }}
                  onBlur={(e) => {
                    e.target.style.background = inputBg;
                    e.target.style.boxShadow = isLightTheme ? "0 1px 3px rgba(0,0,0,0.07)" : "none";
                  }}
                />
              </div>

              <div style={{ position: "relative" }}>
                <input
                  className="animate-on-mount"
                  type="email"
                  name="email"
                  autoComplete="username email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (e.target.value.trim()) validateEmail(e.target.value);
                  }}
                  placeholder={translations[lang]?.email || "Email"}
                  required
                  style={{
                    background: inputBg,
                    color: textColor,
                    border: inputBorder,
                    borderRadius: "10px",
                    padding: isMobile ? "13px 18px" : "16px 20px",
                    fontSize: "1.08rem",
                    outline: "none",
                    width: "100%",
                    transition: "all 0.28s ease",
                    boxShadow: isLightTheme ? "0 1px 3px rgba(0,0,0,0.07)" : "none",
                  }}
                  onFocus={(e) => {
                    e.target.style.background = inputFocusBg;
                    e.target.style.boxShadow = inputFocusShadow;
                  }}
                  onBlur={(e) => {
                    e.target.style.background = inputBg;
                    e.target.style.boxShadow = isLightTheme ? "0 1px 3px rgba(0,0,0,0.07)" : "none";
                  }}
                />
                {emailError && (
                  <div style={{ color: isLightTheme ? "#ef4444" : "#f87171", fontSize: "0.94rem", marginTop: "6px" }}>
                    {emailError}
                  </div>
                )}
              </div>

              <div style={{ position: "relative" }}>
                <input
                  className="animate-on-mount"
                  type={showPassword ? "text" : "password"}
                  name="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={translations[lang]?.password || "Password"}
                  required
                  style={{
                    background: inputBg,
                    color: textColor,
                    border: inputBorder,
                    borderRadius: "10px",
                    padding: isMobile ? "13px 44px 13px 18px" : "16px 52px 16px 20px",
                    fontSize: "1.08rem",
                    outline: "none",
                    width: "100%",
                    transition: "all 0.28s ease",
                    boxShadow: isLightTheme ? "0 1px 3px rgba(0,0,0,0.07)" : "none",
                  }}
                  onFocus={(e) => {
                    e.target.style.background = inputFocusBg;
                    e.target.style.boxShadow = inputFocusShadow;
                  }}
                  onBlur={(e) => {
                    e.target.style.background = inputBg;
                    e.target.style.boxShadow = isLightTheme ? "0 1px 3px rgba(0,0,0,0.07)" : "none";
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: "absolute",
                    right: "16px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                    padding: "0",
                    width: "30px",
                    height: "30px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    opacity: 0.85,
                    transition: "opacity 0.2s",
                    borderRadius: "6px",
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.opacity = "1"}
                  onMouseLeave={(e) => e.currentTarget.style.opacity = "0.85"}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  <EyeIcon isOpen={showPassword} color={iconColor} />
                </button>
              </div>

              <div style={{ position: "relative" }}>
                <input
                  className="animate-on-mount"
                  type={showConfirmPassword ? "text" : "password"}
                  name="confirm-password"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder={translations[lang]?.confirmPassword || "Confirm Password"}
                  required
                  style={{
                    background: inputBg,
                    color: textColor,
                    border: inputBorder,
                    borderRadius: "10px",
                    padding: isMobile ? "13px 44px 13px 18px" : "16px 52px 16px 20px",
                    fontSize: "1.08rem",
                    outline: "none",
                    width: "100%",
                    transition: "all 0.28s ease",
                    boxShadow: isLightTheme ? "0 1px 3px rgba(0,0,0,0.07)" : "none",
                  }}
                  onFocus={(e) => {
                    e.target.style.background = inputFocusBg;
                    e.target.style.boxShadow = inputFocusShadow;
                  }}
                  onBlur={(e) => {
                    e.target.style.background = inputBg;
                    e.target.style.boxShadow = isLightTheme ? "0 1px 3px rgba(0,0,0,0.07)" : "none";
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  style={{
                    position: "absolute",
                    right: "16px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                    padding: "0",
                    width: "30px",
                    height: "30px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    opacity: 0.85,
                    transition: "opacity 0.2s",
                    borderRadius: "6px",
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.opacity = "1"}
                  onMouseLeave={(e) => e.currentTarget.style.opacity = "0.85"}
                  aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                >
                  <EyeIcon isOpen={showConfirmPassword} color={iconColor} />
                </button>
                {confirmPasswordError && (
                  <div style={{ color: isLightTheme ? "#ef4444" : "#f87171", fontSize: "0.94rem", marginTop: "6px" }}>
                    {confirmPasswordError}
                  </div>
                )}
              </div>

              {/* Password strength bar — AFTER both password fields, matching reference */}
              <div style={{ marginTop: isMobile ? "-2px" : "-4px" }}>
                <div
                  style={{
                    height: "8px",
                    width: "100%",
                    background: passwordStrength === 0
                      ? (isLightTheme ? "#14532d" : "#14532d")
                      : (isLightTheme ? "rgba(0,0,0,0.06)" : "rgba(255,255,255,0.09)"),
                    borderRadius: "4px",
                    overflow: "hidden",
                    position: "relative",
                    boxShadow: isLightTheme ? "0 1px 2px rgba(0,0,0,0.1)" : "0 2px 6px rgba(0,0,0,0.15)",
                  }}
                >
                  <div
                    style={{
                      height: "100%",
                      width: `${passwordStrength === 0 ? 100 : passwordStrength}%`,
                      background: passwordStrength === 0
                        ? "#14532d"
                        : `
                          radial-gradient(circle at 30% 40%, #10b981 0%, transparent 40%),
                          radial-gradient(circle at 70% 60%, #6366f1 0%, transparent 45%),
                          radial-gradient(circle at 20% 80%, #8b5cf6 0%, transparent 38%),
                          radial-gradient(circle at 80% 30%, #ec4899 0%, transparent 42%),
                          linear-gradient(160deg, #22d3ee 0%, #c084fc 100%)
                        `,
                      backgroundSize: "180% 180%, 200% 200%, 220% 220%, 190% 190%, 300% 300%",
                      backgroundPosition: "0% 0%, 100% 100%, 50% 0%, 0% 100%, 50% 50%",
                      opacity: 0.85,
                      animation: passwordStrength === 0 ? "none" : "lavaDrift 38s linear infinite",
                      filter: passwordStrength === 0 ? "none" : "blur(1.5px) saturate(1.3)",
                      mixBlendMode: passwordStrength === 0 ? "normal" : "screen",
                      borderRadius: "4px",
                      transition: "width 0.4s cubic-bezier(0.19, 1, 0.22, 1)",
                      position: "relative",
                      zIndex: 2,
                    }}
                  />
                  <div
                    style={{
                      position: "absolute",
                      top: "-2px",
                      left: 0,
                      right: 0,
                      bottom: "-2px",
                      background: `
                        radial-gradient(circle at ${Math.max(passwordStrength, 5)}% 50%, 
                        ${passwordStrength > 66 ? '#4ade80' : passwordStrength > 33 ? '#f59e0b' : passwordStrength === 0 ? '#14532d' : '#f87171'} 0%, 
                        transparent 70%)
                      `,
                      filter: "blur(8px)",
                      opacity: passwordStrength > 0 ? 0.6 : 0.5,
                      zIndex: 1,
                    }}
                  />
                </div>
                <div
                  style={{
                    marginTop: "6px",
                    fontSize: "0.88rem",
                    fontWeight: 600,
                    color: getStrengthColor(passwordStrength),
                    textAlign: "center",
                    textShadow: isLightTheme ? "none" : "0 1px 2px rgba(0,0,0,0.3)",
                    letterSpacing: "0.3px",
                  }}
                >
                  {getStrengthText(passwordStrength)}{passwordStrength > 0 && ` • ${passwordStrength}%`}
                </div>
              </div>

              <div style={{ position: "relative" }}>
                <input
                  className="animate-on-mount"
                  type="text"
                  value={inviteCode}
                  onChange={handleInviteCodeChange}
                  placeholder={translations[lang]?.enterInviteCode || "Invite Code"}
                  readOnly={isInviteCodeAutoFilled && inviteCodeValid}
                  style={{
                    background: inputBg,
                    color: textColor,
                    borderRadius: "10px",
                    padding: isMobile ? "13px 18px" : "16px 20px",
                    fontSize: "1.08rem",
                    outline: "none",
                    width: "100%",
                    transition: "all 0.28s ease",
                    boxShadow: isLightTheme ? "0 1px 3px rgba(0,0,0,0.07)" : "none",
                    border: inviteCode.trim()
                      ? inviteCodeValid
                        ? `2px solid ${isLightTheme ? "#22c55e" : "#4CAF50"}`
                        : `2px solid ${isLightTheme ? "#ef4444" : "#f87171"}`
                      : "none",
                    cursor: (isInviteCodeAutoFilled && inviteCodeValid) ? "not-allowed" : "text",
                    opacity: (isInviteCodeAutoFilled && inviteCodeValid) ? 0.92 : 1,
                  }}
                  onFocus={(e) => {
                    if (!(isInviteCodeAutoFilled && inviteCodeValid)) {
                      e.target.style.background = inputFocusBg;
                      e.target.style.boxShadow = inputFocusShadow;
                    }
                  }}
                  onBlur={(e) => {
                    if (!(isInviteCodeAutoFilled && inviteCodeValid)) {
                      e.target.style.background = inputBg;
                      e.target.style.boxShadow = isLightTheme
                        ? "0 1px 3px rgba(0,0,0,0.07)"
                        : "none";
                    }
                  }}
                />
                {inviteCode.trim() && (
                  <div
                    style={{
                      color: inviteCodeValid
                        ? (isLightTheme ? "#16a34a" : "#4CAF50")
                        : (isLightTheme ? "#dc2626" : "#f87171"),
                      fontSize: "0.94rem",
                      marginTop: "6px",
                      textAlign: "center",
                      fontWeight: 500,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "6px"
                    }}
                  >
                    {inviteCodeValid ? (
                      <>
                        <span>✓</span> {translations[lang]?.validInviteCode || "Valid invite code"}
                      </>
                    ) : (
                      <>
                        <span>⚠️</span> {translations[lang]?.invalidInviteCode || "Invalid invite code"}
                      </>
                    )}
                  </div>
                )}
              </div>

              <div
                className="animate-on-mount"
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "10px",
                  marginTop: "2px",
                  cursor: "pointer",
                  userSelect: "none"
                }}
                onClick={() => setAgreePrivacy(!agreePrivacy)}
              >
                <div style={agreePrivacy ? checkedStyle : checkboxStyle}>
                  {agreePrivacy && (
                    <div style={{
                      width: "8px",
                      height: "8px",
                      borderRadius: "2px",
                      background: "white",
                      boxShadow: isLightTheme ? "0 1px 2px rgba(0,0,0,0.2)" : "none"
                    }} />
                  )}
                </div>
                <span style={{
                  fontSize: "0.92rem",
                  color: textColor,
                  lineHeight: "1.4"
                }}>
                  {translations[lang]?.agreePrivacy || "I agree to the "}
                  <a
                    href="/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      color: themeVars.accent,
                      textDecoration: "underline",
                      cursor: "pointer",
                      fontWeight: 500
                    }}
                  >
                    {translations[lang]?.privacyPolicy || "Privacy Policy"}
                  </a>
                </span>
              </div>

              {termsError && (
                <div style={{
                  color: isLightTheme ? "#ef4444" : "#f87171",
                  fontSize: "0.92rem",
                  marginTop: "-6px",
                  textAlign: "center"
                }}>
                  {termsError}
                </div>
              )}

              {isSamsungBrowser ? (
                <button
                  className="animate-on-mount"
                  type="submit"
                  style={{
                    width: "100%",
                    margin: "12px 0 0",
                    display: "block",
                    border: "none",
                    borderRadius: 36,
                    cursor: "pointer",
                    background: "rgba(220,222,248,0.92)",
                    color: "#3730a3",
                    padding: isMobile ? "14px" : "17px",
                    fontSize: "1.12rem",
                    fontWeight: 600,
                    transition: "background 0.25s ease, box-shadow 0.25s ease",
                    boxShadow: "0 4px 16px rgba(99,102,241,0.15)",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "rgba(205,208,242,0.97)";
                    e.currentTarget.style.boxShadow = "0 6px 22px rgba(99,102,241,0.22)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "rgba(220,222,248,0.92)";
                    e.currentTarget.style.boxShadow = "0 4px 16px rgba(99,102,241,0.15)";
                  }}
                >
                  {pendingOAuth
                    ? translations[lang]?.completeRegistration || "Complete Registration"
                    : lang === "fr" ? "S'inscrire" : "Register"}
                </button>
              ) : (
                <button
                  className="animate-on-mount"
                  type="submit"
                  style={{
                    width: "100%",
                    margin: "12px 0 0",
                    border: "none",
                    borderRadius: 36,
                    overflow: "hidden",
                    position: "relative",
                    cursor: "pointer",
                    background: "transparent",
                    padding: 0,
                    isolation: "isolate",
                    transition: "all 0.2s ease",
                    boxShadow: isButtonPressed
                      ? "0 15px 60px rgba(99,102,241,0.4)"
                      : isLightTheme
                        ? "0 10px 25px -5px rgba(0, 0, 0, 0.15), 0 8px 10px -6px rgba(0, 0, 0, 0.1)"
                        : "0 25px 90px rgba(99,102,241,0.3)",
                    transform: isButtonPressed ? "translateY(2px) scale(0.985)" : "translateY(0)",
                    display: "block",
                  }}
                  onMouseEnter={(e) => {
                    if (!isButtonPressed) {
                      e.currentTarget.style.transform = isLightTheme
                        ? "translateY(-2px) scale(1.015)"
                        : "translateY(-12px) scale(1.022)";
                      e.currentTarget.style.boxShadow = isLightTheme
                        ? "0 15px 35px -5px rgba(0, 0, 0, 0.2), 0 10px 15px -6px rgba(0, 0, 0, 0.15)"
                        : "0 50px 140px rgba(99,102,241,0.55)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isButtonPressed) {
                      e.currentTarget.style.transform = "translateY(0) scale(1)";
                      e.currentTarget.style.boxShadow = isLightTheme
                        ? "0 10px 25px -5px rgba(0, 0, 0, 0.15), 0 8px 10px -6px rgba(0, 0, 0, 0.1)"
                        : "0 25px 90px rgba(99,102,241,0.3)";
                  }
                  }}
                  onMouseDown={() => setIsButtonPressed(true)}
                  onMouseUp={() => setIsButtonPressed(false)}
                  onTouchStart={() => setIsButtonPressed(true)}
                  onTouchEnd={() => setIsButtonPressed(false)}
                >
                  <div
                    className="lava-core"
                    style={{
                      position: "absolute",
                      inset: 0,
                      background: `
                        radial-gradient(circle at 30% 40%, #10b981 0%, transparent 40%),
                        radial-gradient(circle at 70% 60%, #6366f1 0%, transparent 45%),
                        radial-gradient(circle at 20% 80%, #8b5cf6 0%, transparent 38%),
                        radial-gradient(circle at 80% 30%, #ec4899 0%, transparent 42%),
                        linear-gradient(160deg, #22d3ee 0%, #c084fc 100%)
                      `,
                      backgroundSize: "180% 180%, 200% 200%, 220% 220%, 190% 190%, 300% 300%",
                      backgroundPosition: "0% 0%, 100% 100%, 50% 0%, 0% 100%, 50% 50%",
                      opacity: 0.75,
                      animation: "lavaDrift 38s linear infinite",
                      filter: "blur(4px) saturate(1.4)",
                      mixBlendMode: "screen",
                    }}
                  />

                  <div
                    className="lava-overlay"
                    style={{
                      position: "absolute",
                      inset: 0,
                      background: `
                        radial-gradient(circle at 55% 25%, #10b98188 0%, transparent 50%),
                        radial-gradient(circle at 15% 65%, #6366f188 0%, transparent 55%),
                        radial-gradient(circle at 85% 75%, #8b5cf688 0%, transparent 48%)
                      `,
                      backgroundSize: "240% 240%, 280% 280%, 260% 260%",
                      opacity: 0.6,
                      animation: "lavaDrift2 52s linear infinite reverse",
                      filter: "blur(6px)",
                      mixBlendMode: "overlay",
                    }}
                  />

                  <div
                    style={{
                      position: "relative",
                      zIndex: 4,
                      padding: isMobile ? "14px" : "17px",
                      color: "#ffffff",
                      textShadow: isLightTheme
                        ? "0 1px 3px rgba(0,0,0,0.35)"
                        : "0 2px 12px rgba(0,0,0,0.7)",
                      fontSize: "1.12rem",
                      fontWeight: 600,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {pendingOAuth
                      ? translations[lang]?.completeRegistration || "Complete Registration"
                      : lang === "fr" ? "S'inscrire" : "Register"}
                  </div>
                </button>
              )}

              {error && (
                <div style={{
                  color: isLightTheme ? "#ef4444" : "#f87171",
                  textAlign: "center",
                  marginTop: "10px",
                  fontSize: "0.96rem",
                  fontWeight: 500
                }}>
                  {error}
                </div>
              )}

              <div
                className="animate-on-mount"
                style={{
                  margin: isMobile ? "8px 0 4px" : "0",
                  textAlign: "center",
                  fontSize: "0.96rem",
                  color: isLightTheme ? "#4b5563" : "inherit",
                  opacity: isLightTheme ? 0.85 : 0.78,
                }}
              >
                {translations[lang]?.or_sign_up_with || "or sign up with"}
              </div>

              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  gap: "12px",
                  justifyContent: "center",
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <button
                  className="animate-on-mount"
                  type="button"
                  onClick={() => handleSocialRegister("google")}
                  style={{
                    background: inputBg,
                    border: inputBorder,
                    borderRadius: "12px",
                    padding: isMobile ? "10px 16px" : "14px 20px",
                    fontSize: isMobile ? "0.96rem" : "1.02rem",
                    color: textColor,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "10px",
                    cursor: "pointer",
                    transition: "all 0.22s ease",
                    flex: isMobile ? "1 1 auto" : "auto",
                    minWidth: isMobile ? "0" : "auto",
                    maxWidth: isMobile ? "48%" : "auto",
                    whiteSpace: "nowrap",
                    boxShadow: isLightTheme ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
                  }}
                  onMouseEnter={(e) => {
                    if (!isMobile) {
                      e.currentTarget.style.background = inputFocusBg;
                      e.currentTarget.style.transform = "scale(1.03)";
                      if (isLightTheme) e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.12)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isMobile) {
                      e.currentTarget.style.background = inputBg;
                      e.currentTarget.style.transform = "scale(1)";
                      if (isLightTheme) e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.08)";
                    }
                  }}
                >
                  <img
                    src="https://www.google.com/favicon.ico"
                    alt="Google"
                    width={18}
                    height={18}
                  />
                  Google
                </button>

                <button
                  className="animate-on-mount"
                  type="button"
                  onClick={() => handleSocialRegister("linkedin")}
                  style={{
                    background: inputBg,
                    border: inputBorder,
                    borderRadius: "12px",
                    padding: isMobile ? "10px 16px" : "14px 20px",
                    fontSize: isMobile ? "0.96rem" : "1.02rem",
                    color: textColor,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "10px",
                    cursor: "pointer",
                    transition: "all 0.22s ease",
                    flex: isMobile ? "1 1 auto" : "auto",
                    minWidth: isMobile ? "0" : "auto",
                    maxWidth: isMobile ? "48%" : "auto",
                    whiteSpace: "nowrap",
                    boxShadow: isLightTheme ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
                  }}
                  onMouseEnter={(e) => {
                    if (!isMobile) {
                      e.currentTarget.style.background = inputFocusBg;
                      e.currentTarget.style.transform = "scale(1.03)";
                      if (isLightTheme) e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.12)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isMobile) {
                      e.currentTarget.style.background = inputBg;
                      e.currentTarget.style.transform = "scale(1)";
                      if (isLightTheme) e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.08)";
                    }
                  }}
                >
                  <img
                    src={linkedinIcon}
                    alt="LinkedIn"
                    width={18}
                    height={18}
                  />
                  LinkedIn
                </button>
              </div>
            </form>
          </div>
        </div>
      </>
    );
  }

  // ════════════════════════════════════════════════════════════════════════════
  // DESKTOP BRANCH — split layout from file 2
  // ════════════════════════════════════════════════════════════════════════════
  return (
    <>
      <style>{`
        ${globalAnimationStyles}

        @keyframes lavaDrift {
          0%,100%{ background-position:0% 0%,100% 100%,50% 0%,0% 100%,50% 50%; }
          50%     { background-position:100% 100%,0% 0%,0% 100%,100% 0%,75% 25%; }
        }
        @keyframes lavaDrift2 {
          0%,100%{ background-position:0% 0%,100% 100%,50% 0%; }
          50%     { background-position:100% 100%,0% 0%,100% 50%; }
        }
        @keyframes gridPulse {
          0%,100%{ opacity:0.35; }
          50%    { opacity:0.55; }
        }
        @keyframes fadeUp {
          from{ opacity:0; transform:translateY(16px); }
          to  { opacity:1; transform:translateY(0); }
        }
        @keyframes statIn {
          from{ opacity:0; transform:translateX(-20px); }
          to  { opacity:1; transform:translateX(0); }
        }
        @keyframes formIn {
          from{ opacity:0; transform:translateX(20px); }
          to  { opacity:1; transform:translateX(0); }
        }

        .rp-stat-card {
          padding: 20px 22px;
          border-radius: 14px;
          background: ${CARD_BG};
          border: 1px solid ${BORDER};
          display: grid;
          grid-template-columns: 1fr auto;
          align-items: center;
          gap: 16px;
          transition: border-color 0.25s, background 0.25s, transform 0.25s;
          animation: statIn 0.55s ease both;
        }
        .rp-stat-card:hover {
          border-color: rgba(255,255,255,0.12);
          background: ${isLightTheme ? "rgba(255,255,255,0.85)" : "rgba(255,255,255,0.055)"};
          transform: translateX(4px);
        }
        .rp-stat-card:nth-child(1){ animation-delay:0.15s; }
        .rp-stat-card:nth-child(2){ animation-delay:0.25s; }
        .rp-stat-card:nth-child(3){ animation-delay:0.35s; }

        .rp-input-field {
          width: 100%;
          background: ${INPUT_BG};
          color: ${TEXT};
          border: 1px solid ${BORDER};
          border-radius: 10px;
          padding: 14px 18px;
          font-size: 0.97rem;
          outline: none;
          transition: all 0.22s ease;
          box-sizing: border-box;
        }
        .rp-input-field:focus {
          background: ${INPUT_FOCUS_BG};
          box-shadow: ${INPUT_SHADOW};
          border-color: ${themeVars.accent}60;
        }
        .rp-input-field::placeholder { color: ${MUTED}; }

        .rp-social-btn {
          flex: 1;
          background: ${INPUT_BG};
          color: ${TEXT};
          border: 1px solid ${BORDER};
          border-radius: 10px;
          padding: 13px 16px;
          font-size: 0.95rem;
          font-weight: 500;
          display: flex; align-items: center; justify-content: center; gap: 9px;
          cursor: pointer;
          transition: all 0.22s ease;
        }
        .rp-social-btn:hover {
          background: ${INPUT_FOCUS_BG};
          border-color: ${themeVars.accent}50;
          transform: translateY(-1px);
        }

        .rp-grid-bg {
          position: absolute; inset: 0;
          background-image:
            linear-gradient(${isLightTheme ? "rgba(99,102,241,0.07)" : "rgba(255,255,255,0.04)"} 1px, transparent 1px),
            linear-gradient(90deg, ${isLightTheme ? "rgba(99,102,241,0.07)" : "rgba(255,255,255,0.04)"} 1px, transparent 1px);
          background-size: 40px 40px;
          animation: gridPulse 6s ease-in-out infinite;
          pointer-events: none;
        }
        @keyframes authDividerPulse {
          0%, 100% { height: 42%; opacity: 0.45; transform: translateY(-50%) scaleY(0.92); }
          50% { height: 68%; opacity: 0.95; transform: translateY(-50%) scaleY(1.06); }
        }
      `}</style>

      <div style={{
        width: "100vw", minHeight: "100dvh",
        background: BG,
        display: "grid",
        gridTemplateColumns: "60% 40%",
        color: TEXT,
        overflow: "hidden",
        paddingTop: "var(--app-header-h, 56px)",
        boxSizing: "border-box",
      }}>

        {/* ═══════════════════════════════════════════════════════ LEFT PANEL */}
        <div style={{
          position: "relative",
          padding: "64px 60px",
          
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}>
          <div style={{
            position: "absolute",
            right: -1,
            top: "50%",
            width: 2,
            height: "52%",
            transform: "translateY(-50%)",
            borderRadius: 999,
            background: isLightTheme
              ? "linear-gradient(to bottom, transparent 0%, rgba(99,102,241,0.95) 22%, rgba(139,92,246,1) 50%, rgba(34,211,238,0.9) 78%, transparent 100%)"
              : "linear-gradient(to bottom, transparent 0%, rgba(167,139,250,0.9) 22%, rgba(139,92,246,1) 50%, rgba(34,211,238,0.95) 78%, transparent 100%)",
            boxShadow: isLightTheme ? "0 0 18px rgba(99,102,241,0.22)" : "0 0 20px rgba(167,139,250,0.22)",
            animation: "authDividerPulse 4.2s ease-in-out infinite",
            pointerEvents: "none",
            zIndex: 3,
          }} />
          {/* bg grid */}
          <div className="rp-grid-bg" />

          {/* glow orb */}
          <div style={{
            position: "absolute", width: 420, height: 420,
            borderRadius: "50%",
            background: isLightTheme
              ? "radial-gradient(circle, rgba(99,102,241,0.18) 0%, transparent 70%)"
              : "radial-gradient(circle, rgba(99,102,241,0.2) 0%, transparent 70%)",
            top: -120, left: -100,
            pointerEvents: "none",
          }} />
          <div style={{
            position: "absolute", width: 280, height: 280,
            borderRadius: "50%",
            background: isLightTheme
              ? "radial-gradient(circle, rgba(34,211,238,0.1) 0%, transparent 70%)"
              : "radial-gradient(circle, rgba(34,211,238,0.12) 0%, transparent 70%)",
            bottom: 60, right: -60,
            pointerEvents: "none",
          }} />

          {/* content */}
          <div style={{ position: "relative", zIndex: 2, flex: 1, display: "flex", flexDirection: "column" }}>
            {/* badge */}
            <div style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              marginBottom: 28,
              animation: "fadeUp 0.5s ease both",
            }}>
              <span style={{
                display: "inline-block", width: 7, height: 7, borderRadius: "50%",
                background: "#4ade80",
                boxShadow: "0 0 8px #4ade80",
              }} />
              <span style={{
                fontSize: 11, fontWeight: 700, letterSpacing: "0.18em",
                color: "#a78bfa", textTransform: "uppercase",
              }}>
                // LIVE NETWORK
              </span>
            </div>

            {/* headline */}
            <div style={{ marginBottom: 36, animation: "fadeUp 0.5s 0.08s ease both" }}>
              <h1 style={{
                fontSize: "clamp(1.9rem, 2.6vw, 2.55rem)",
                fontWeight: 700, lineHeight: 1.1,
                letterSpacing: "-0.025em", margin: 0,
              }}>
                You're not joining a&nbsp;form.<br />
                <span style={{ color: MUTED }}>You're joining</span>{" "}
                <span style={{
                  background: "linear-gradient(135deg, #a78bfa 0%, #22d3ee 100%)",
                  WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
                }}>
                  this
                </span>.
              </h1>
            </div>

            {/* stat cards */}
            {statRows && (
              <div style={{ display: "flex", flexDirection: "column", gap: 14, flex: 1 }}>
                {statRows.map((r, i) => (
                  <div className="rp-stat-card" key={i}>
                    <div>
                      <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginBottom: 3 }}>
                        <span style={{
                          fontSize: "clamp(2.6rem, 3.6vw, 3.4rem)",
                          fontWeight: 700, letterSpacing: "-0.03em",
                          lineHeight: 1, color: r.color,
                          fontVariantNumeric: "tabular-nums",
                        }}>
                          {r.n}
                        </span>
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: TEXT }}>{r.l}</div>
                      <div style={{ fontSize: 12, color: MUTED, marginTop: 2 }}>{r.sub}</div>
                    </div>
                    <Sparkline color={r.color} points={r.spark} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ══════════════════════════════════════════════════════ RIGHT PANEL */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "center",
          padding: "40px 48px",
          animation: "formIn 0.55s ease both",
        }}>
          <div style={{ width: "100%", maxWidth: 420 }}>

            {/* title */}
            <h2 style={{
              margin: "0 0 28px",
              fontSize: "clamp(1.6rem, 2.2vw, 2rem)",
              fontWeight: 700, textAlign: "center",
              letterSpacing: "-0.02em",
              ...(isSamsungBrowser
                ? { color: "#3730a3" }
                : isLightTheme
                ? { background: themeVars.btnBg, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }
                : { background: "linear-gradient(135deg, #a78bfa, #22d3ee)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }
              ),
            }}>
              {translations[lang]?.register || "Sign Up"}
            </h2>

            {/* OAuth pending notice */}
            {pendingOAuth && (
              <div style={{
                background: isLightTheme ? "rgba(99,102,241,0.08)" : "rgba(99,102,241,0.12)",
                border: `1px solid ${themeVars.accent}35`,
                borderRadius: 10, padding: "12px 16px",
                marginBottom: 18, fontSize: "0.9rem",
                color: isLightTheme ? "#3730a3" : "#a78bfa",
                textAlign: "center",
              }}>
                ℹ️ Completing {oauthProvider} registration — please enter your invite code.
              </div>
            )}

            <form
              key={lang}
              onSubmit={handleSubmit}
              autoComplete="on"
              style={{ display: "flex", flexDirection: "column", gap: 13 }}
            >
              {/* Name */}
              <input
                className="rp-input-field"
                type="text" name="name" autoComplete="given-name"
                value={username} onChange={(e) => setUsername(e.target.value)}
                placeholder={translations[lang]?.name || "Full Name"}
                required
              />

              {/* Email */}
              <div>
                <input
                  className="rp-input-field"
                  type="email" name="email" autoComplete="username email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); if (e.target.value.trim()) validateEmail(e.target.value); }}
                  placeholder={translations[lang]?.email || "E-mail"}
                  required
                />
                {emailError && (
                  <div style={{ color: "#f87171", fontSize: "0.85rem", marginTop: 5, paddingLeft: 4 }}>
                    {emailError}
                  </div>
                )}
              </div>

              {/* Password */}
              <div style={{ position: "relative" }}>
                <input
                  className="rp-input-field"
                  type={showPassword ? "text" : "password"}
                  name="password" autoComplete="new-password"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder={translations[lang]?.password || "Password"}
                  required
                  style={{ paddingRight: 48 }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)",
                    background: "none", border: "none", cursor: "pointer", padding: 0,
                    display: "flex", alignItems: "center", opacity: 0.7, transition: "opacity 0.2s",
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.opacity = "1"}
                  onMouseLeave={(e) => e.currentTarget.style.opacity = "0.7"}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  <EyeIcon isOpen={showPassword} color={ICON_CLR} />
                </button>
              </div>

              {/* Confirm Password */}
              <div style={{ position: "relative" }}>
                <input
                  className="rp-input-field"
                  type={showConfirmPassword ? "text" : "password"}
                  name="confirm-password" autoComplete="new-password"
                  value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder={translations[lang]?.confirmPassword || "Confirm Password"}
                  required
                  style={{ paddingRight: 48 }}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  style={{
                    position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)",
                    background: "none", border: "none", cursor: "pointer", padding: 0,
                    display: "flex", alignItems: "center", opacity: 0.7, transition: "opacity 0.2s",
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.opacity = "1"}
                  onMouseLeave={(e) => e.currentTarget.style.opacity = "0.7"}
                  aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                >
                  <EyeIcon isOpen={showConfirmPassword} color={ICON_CLR} />
                </button>
                {confirmPasswordError && (
                  <div style={{ color: "#f87171", fontSize: "0.85rem", marginTop: 5, paddingLeft: 4 }}>
                    {confirmPasswordError}
                  </div>
                )}
              </div>

              {/* Password strength bar */}
              <div style={{ marginTop: -2 }}>
                <div style={{
                  height: 6, width: "100%", borderRadius: 3,
                  background: isLightTheme ? "rgba(0,0,0,0.07)" : "rgba(255,255,255,0.07)",
                  overflow: "hidden",
                }}>
                  <div style={{
                    height: "100%",
                    width: `${passwordStrength === 0 ? 100 : passwordStrength}%`,
                    background: passwordStrength === 0
                      ? (isLightTheme ? "#d1d5db" : "#1f2937")
                      : strengthColor,
                    borderRadius: 3,
                    transition: "width 0.4s cubic-bezier(0.19,1,0.22,1), background 0.3s ease",
                  }} />
                </div>
                <div style={{
                  marginTop: 5, fontSize: "0.82rem", fontWeight: 600,
                  color: passwordStrength === 0 ? MUTED : strengthColor,
                  textAlign: "center",
                }}>
                  {strengthLabel}{passwordStrength > 0 && ` · ${passwordStrength}%`}
                </div>
              </div>

              {/* Invite code */}
              <div>
                <input
                  className="rp-input-field"
                  type="text"
                  value={inviteCode}
                  onChange={handleInviteCodeChange}
                  placeholder={translations[lang]?.enterInviteCode || "Enter Invite code"}
                  readOnly={isInviteCodeAutoFilled && inviteCodeValid}
                  style={{
                    cursor: (isInviteCodeAutoFilled && inviteCodeValid) ? "not-allowed" : "text",
                    opacity: (isInviteCodeAutoFilled && inviteCodeValid) ? 0.85 : 1,
                    borderColor: inviteCode.trim()
                      ? inviteCodeValid
                        ? "rgba(74,222,128,0.55)"
                        : "rgba(248,113,113,0.55)"
                      : BORDER,
                  }}
                />
                {inviteCode.trim() && (
                  <div style={{
                    marginTop: 5, fontSize: "0.85rem", fontWeight: 500,
                    color: inviteCodeValid ? "#4ade80" : "#f87171",
                    display: "flex", alignItems: "center", gap: 5, paddingLeft: 4,
                  }}>
                    {inviteCodeValid
                      ? <><span>✓</span> {translations[lang]?.validInviteCode || "Valid invite code"}</>
                      : <><span>⚠</span> {translations[lang]?.invalidInviteCode || "Invalid invite code"}</>
                    }
                  </div>
                )}
              </div>

              {/* Privacy checkbox */}
              <div
                style={{
                  display: "flex", alignItems: "flex-start", gap: 10,
                  cursor: "pointer", userSelect: "none",
                }}
                onClick={() => setAgreePrivacy(!agreePrivacy)}
              >
                <div style={{
                  width: 18, height: 18, borderRadius: 5, flexShrink: 0, marginTop: 1,
                  border: `2px solid ${agreePrivacy ? themeVars.accent : BORDER}`,
                  background: agreePrivacy ? themeVars.accent : "transparent",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  transition: "all 0.2s ease",
                }}>
                  {agreePrivacy && (
                    <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                      <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </div>
                <span style={{ fontSize: "0.88rem", color: TEXT, lineHeight: 1.45 }}>
                  {translations[lang]?.agreePrivacy || "I agree to the "}
                  <a
                    href="/privacy-policy" target="_blank" rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    style={{ color: themeVars.accent, textDecoration: "underline", fontWeight: 500 }}
                  >
                    {translations[lang]?.privacyPolicy || "Privacy Policy"}
                  </a>
                </span>
              </div>
              {termsError && (
                <div style={{ color: "#f87171", fontSize: "0.85rem", marginTop: -6, textAlign: "center" }}>
                  {termsError}
                </div>
              )}

              {/* Submit button */}
              {isSamsungBrowser ? (
                <button
                  type="submit"
                  style={{
                    width: "100%", marginTop: 6, border: "none", borderRadius: 36,
                    cursor: "pointer", background: "rgba(220,222,248,0.92)", color: "#3730a3",
                    padding: "14px", fontSize: "1rem", fontWeight: 600,
                    transition: "all 0.25s ease",
                  }}
                >
                  {pendingOAuth ? translations[lang]?.completeRegistration || "Complete Registration" : lang === "fr" ? "S'inscrire" : "Register"}
                </button>
              ) : (
                <button
                  type="submit"
                  style={{
                    width: "100%", marginTop: 6,
                    border: "none", borderRadius: 36, overflow: "hidden",
                    position: "relative", cursor: "pointer",
                    background: "transparent", padding: 0,
                    isolation: "isolate",
                    transition: "transform 0.2s ease, box-shadow 0.2s ease",
                    boxShadow: isButtonPressed
                      ? "0 8px 32px rgba(99,102,241,0.45)"
                      : isLightTheme
                        ? "0 8px 24px rgba(0,0,0,0.12)"
                        : "0 16px 48px rgba(99,102,241,0.28)",
                    transform: isButtonPressed ? "translateY(2px) scale(0.985)" : "translateY(0) scale(1)",
                  }}
                  onMouseEnter={(e) => {
                    if (!isButtonPressed) {
                      e.currentTarget.style.transform = "translateY(-2px) scale(1.012)";
                      e.currentTarget.style.boxShadow = isLightTheme
                        ? "0 12px 32px rgba(0,0,0,0.18)"
                        : "0 24px 72px rgba(99,102,241,0.45)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isButtonPressed) {
                      e.currentTarget.style.transform = "translateY(0) scale(1)";
                      e.currentTarget.style.boxShadow = isLightTheme
                        ? "0 8px 24px rgba(0,0,0,0.12)"
                        : "0 16px 48px rgba(99,102,241,0.28)";
                    }
                  }}
                  onMouseDown={() => setIsButtonPressed(true)}
                  onMouseUp={() => setIsButtonPressed(false)}
                  onTouchStart={() => setIsButtonPressed(true)}
                  onTouchEnd={() => setIsButtonPressed(false)}
                >
                  {/* lava gradient bg */}
                  <div style={{
                    position: "absolute", inset: 0,
                    background: `
                      radial-gradient(circle at 30% 40%, #10b981 0%, transparent 40%),
                      radial-gradient(circle at 70% 60%, #6366f1 0%, transparent 45%),
                      radial-gradient(circle at 20% 80%, #8b5cf6 0%, transparent 38%),
                      radial-gradient(circle at 80% 30%, #ec4899 0%, transparent 42%),
                      linear-gradient(160deg, #22d3ee 0%, #c084fc 100%)
                    `,
                    backgroundSize: "180% 180%, 200% 200%, 220% 220%, 190% 190%, 300% 300%",
                    backgroundPosition: "0% 0%, 100% 100%, 50% 0%, 0% 100%, 50% 50%",
                    opacity: 0.85,
                    animation: "lavaDrift 38s linear infinite",
                    filter: "blur(4px) saturate(1.4)",
                    mixBlendMode: "screen",
                  }} />
                  <div style={{
                    position: "absolute", inset: 0,
                    background: `
                      radial-gradient(circle at 55% 25%, #10b98188 0%, transparent 50%),
                      radial-gradient(circle at 15% 65%, #6366f188 0%, transparent 55%),
                      radial-gradient(circle at 85% 75%, #8b5cf688 0%, transparent 48%)
                    `,
                    backgroundSize: "240% 240%, 280% 280%, 260% 260%",
                    opacity: 0.6,
                    animation: "lavaDrift2 52s linear infinite reverse",
                    filter: "blur(6px)",
                    mixBlendMode: "overlay",
                  }} />
                  <div style={{
                    position: "relative", zIndex: 4,
                    padding: "15px",
                    color: "#fff",
                    textShadow: isLightTheme ? "0 1px 3px rgba(0,0,0,0.35)" : "0 2px 12px rgba(0,0,0,0.7)",
                    fontSize: "1rem", fontWeight: 600,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    {pendingOAuth
                      ? translations[lang]?.completeRegistration || "Complete Registration"
                      : lang === "fr" ? "S'inscrire" : "Register"}
                  </div>
                </button>
              )}

              {/* General error */}
              {error && (
                <div style={{
                  color: "#f87171", textAlign: "center",
                  fontSize: "0.9rem", fontWeight: 500,
                }}>
                  {error}
                </div>
              )}

              {/* Divider */}
              <div style={{ textAlign: "center", fontSize: "0.88rem", color: MUTED }}>
                {translations[lang]?.or_sign_up_with || "or sign up with"}
              </div>

              {/* Social buttons */}
              <div style={{ display: "flex", gap: 10 }}>
                <button className="rp-social-btn" type="button" onClick={() => handleSocialRegister("google")}>
                  <img src="https://www.google.com/favicon.ico" alt="Google" width={17} height={17} />
                  Google
                </button>
                <button className="rp-social-btn" type="button" onClick={() => handleSocialRegister("linkedin")}>
                  <img src={linkedinIcon} alt="LinkedIn" width={17} height={17} />
                  LinkedIn
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}